import {
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTranslation } from 'react-i18next';

import { useIsTouchUi } from '@/shared/lib/media';

import { Popover } from './Popover';
import styles from './Combobox.module.css';

export interface ComboboxOption {
  value: string;
  /** What is shown and matched by the search. */
  label: string;
  /** A second, quieter line (login, description ...), also searched. */
  description?: string;
  /** Options with the same group are listed under one heading, in first-seen order. */
  group?: string;
  /** Leading visual (avatar, color dot ...). */
  leading?: ReactNode;
  /** Style of the label itself (a font name drawn in its own family). */
  labelStyle?: CSSProperties;
  disabled?: boolean;
}

interface Props {
  /** Accessible name of the control (also the name of the popup). */
  label: string;
  options: readonly ComboboxOption[];
  value: string | null;
  onChange: (value: string) => void;
  /** Shown when nothing is selected. */
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  /** Trigger id (so an outer <label htmlFor> can name it). */
  id?: string;
  /** Visual size of the trigger. */
  size?: 'md' | 'sm';
  className?: string;
  /** At most this many rows are drawn; the rest need a more specific search. */
  limit?: number;
  /** Show the filter field (default). A short list can hide it: the keys still work. */
  searchable?: boolean;
}

const DEFAULT_LIMIT = 100;

/**
 * A styled, searchable single-select: a trigger button that opens a popup with a filter field and a
 * listbox. Keyboard: arrows / Home / End move, Enter chooses, Escape closes (focus returns to the
 * trigger). The listbox uses `aria-activedescendant`, so the filter field keeps focus while the
 * options are navigated. It replaces unstyled native selects wherever the choice list can be long.
 */
export function Combobox({
  label,
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  disabled,
  id,
  size = 'md',
  className,
  limit = DEFAULT_LIMIT,
  searchable = true,
}: Props) {
  const { t } = useTranslation();
  const selected = options.find((option) => option.value === value) ?? null;
  return (
    <Popover
      label={label}
      width={320}
      className={[styles.root, className].filter(Boolean).join(' ')}
      trigger={(props, open) => (
        <button
          {...props}
          id={id}
          type="button"
          className={[styles.trigger, size === 'sm' && styles.small].filter(Boolean).join(' ')}
          disabled={disabled}
          aria-label={`${label}: ${selected?.label ?? placeholder ?? ''}`.trim()}
          data-open={open ? 'true' : undefined}
        >
          {selected?.leading}
          <span
            className={styles.value}
            data-placeholder={selected ? undefined : 'true'}
            {...(selected?.labelStyle && { style: selected.labelStyle })}
          >
            {selected?.label ?? placeholder ?? ''}
          </span>
          <span aria-hidden className={styles.chevron}>
            ▾
          </span>
        </button>
      )}
    >
      {(close) => (
        <ComboboxList
          label={label}
          options={options}
          value={value}
          searchPlaceholder={searchPlaceholder ?? t('common.search')}
          emptyText={emptyText ?? t('common.noResults')}
          limit={limit}
          searchable={searchable}
          onPick={(next) => {
            onChange(next);
            close();
          }}
        />
      )}
    </Popover>
  );
}

function matches(option: ComboboxOption, query: string): boolean {
  if (query === '') return true;
  const haystack = `${option.label}\n${option.description ?? ''}`.toLocaleLowerCase();
  return query
    .toLocaleLowerCase()
    .split(/\s+/)
    .every((part) => haystack.includes(part));
}

/** The popup content of a {@link Combobox} (search field + listbox), for a popover with its own trigger. */
export function ComboboxList({
  label,
  options,
  value,
  searchPlaceholder,
  emptyText,
  limit,
  searchable,
  onPick,
}: {
  label: string;
  options: readonly ComboboxOption[];
  value: string | null;
  searchPlaceholder: string | undefined;
  emptyText: string | undefined;
  limit: number;
  searchable: boolean;
  onPick: (value: string) => void;
}) {
  const [query, setQuery] = useState('');
  const baseId = useId();
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const visible = useMemo(
    () => options.filter((option) => matches(option, query.trim())).slice(0, limit),
    [options, query, limit],
  );
  const enabled = visible.filter((option) => !option.disabled);
  const [active, setActive] = useState<string | null>(value);
  const current =
    active !== null && enabled.some((option) => option.value === active)
      ? active
      : (enabled[0]?.value ?? null);

  // Desktop: type right away. Touch: focus the list itself (arrows / Enter still work with a hardware
  // keyboard) - never the field, which would raise the on-screen keyboard unasked; a tap on the
  // search field focuses it deliberately.
  const touch = useIsTouchUi();
  useEffect(() => {
    (touch ? list.current : input.current)?.focus({ preventScroll: true });
  }, [touch]);
  useEffect(() => {
    if (current === null) return;
    const target = [...(list.current?.querySelectorAll<HTMLElement>('[data-value]') ?? [])].find(
      (element) => element.dataset['value'] === current,
    );
    // Not implemented by every environment (jsdom): a missing scroll is harmless.
    if (typeof target?.scrollIntoView === 'function') target.scrollIntoView({ block: 'nearest' });
  }, [current]);

  const move = (delta: number | 'start' | 'end') => {
    if (enabled.length === 0) return;
    const index = enabled.findIndex((option) => option.value === current);
    let next: number;
    if (delta === 'start') next = 0;
    else if (delta === 'end') next = enabled.length - 1;
    else next = Math.min(enabled.length - 1, Math.max(0, index + delta));
    setActive(enabled[next]?.value ?? null);
  };

  const groups: { name: string | null; items: ComboboxOption[] }[] = [];
  for (const option of visible) {
    const name = option.group ?? null;
    let group = groups.find((g) => g.name === name);
    if (!group) {
      group = { name, items: [] };
      groups.push(group);
    }
    group.items.push(option);
  }
  const optionId = (optionValue: string) => `${baseId}-${optionValue}`;
  const onKey = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      move(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      move(-1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      move('start');
    } else if (event.key === 'End') {
      event.preventDefault();
      move('end');
    } else if (event.key === 'Enter' && current !== null) {
      event.preventDefault();
      onPick(current);
    }
  };

  return (
    <div className={styles.panel}>
      <input
        ref={input}
        className={[styles.search, !searchable && 'visually-hidden'].filter(Boolean).join(' ')}
        type="text"
        role="combobox"
        aria-label={label}
        aria-expanded="true"
        aria-controls={`${baseId}-list`}
        aria-autocomplete="list"
        {...(current !== null && { 'aria-activedescendant': optionId(current) })}
        placeholder={searchPlaceholder}
        autoComplete="off"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
        }}
        onKeyDown={onKey}
      />
      <ul
        ref={list}
        id={`${baseId}-list`}
        role="listbox"
        aria-label={label}
        tabIndex={-1}
        // On a touch screen the list owns the focus (see above), so it names the active option.
        {...(current !== null && { 'aria-activedescendant': optionId(current) })}
        onKeyDown={onKey}
        className={styles.list}
      >
        {groups.map((group) => (
          <li key={group.name ?? ''} role="presentation" className={styles.group}>
            {group.name !== null && (
              <span className={styles.heading} role="presentation">
                {group.name}
              </span>
            )}
            <ul role="group" aria-label={group.name ?? label} className={styles.items}>
              {group.items.map((option) => (
                <li
                  key={option.value}
                  id={optionId(option.value)}
                  role="option"
                  data-value={option.value}
                  aria-selected={option.value === value}
                  aria-disabled={option.disabled ? 'true' : undefined}
                  data-active={option.value === current ? 'true' : undefined}
                  className={styles.option}
                  onPointerEnter={() => {
                    if (!option.disabled) setActive(option.value);
                  }}
                  onClick={() => {
                    if (!option.disabled) onPick(option.value);
                  }}
                >
                  {option.leading}
                  <span className={styles.text}>
                    <span
                      className={styles.name}
                      {...(option.labelStyle && { style: option.labelStyle })}
                    >
                      {option.label}
                    </span>
                    {option.description && (
                      <span className={styles.description}>{option.description}</span>
                    )}
                  </span>
                  {option.value === value && (
                    <span aria-hidden className={styles.check}>
                      ✓
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </li>
        ))}
        {visible.length === 0 && <li className={styles.empty}>{emptyText ?? ''}</li>}
      </ul>
    </div>
  );
}
